import React, { useEffect, useState } from "react";
import {
  Activity,
  Calendar,
  DollarSign,
  Download,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BuyRevenueReportsResponse,
  buyAdminApi,
} from "../../../services/buyAdminApi";
import BuyAdminThemeToggle from "../shared/BuyAdminThemeToggle";
import "../shared/buyAdminThemeGlobals.css";
import styles from "./RevenueReports.module.css";

const emptyMonth = {
  month: "",
  mrr: 0,
  arr: 0,
  newMrr: 0,
  churnMrr: 0,
  netMrr: 0,
  members: 0,
  ticketFees: 0,
  premiumFeatures: 0,
  subscriptions: 0,
  other: 0,
  grossRevenue: 0,
  memberSavings: 0,
  orders: 0,
};

const emptyReports: BuyRevenueReportsResponse = {
  window: { start: "", end: "" },
  currentMonth: emptyMonth,
  previousMonth: emptyMonth,
  mrrGrowth: 0,
  monthlyData: [],
  revenueBreakdown: [],
};

const formatCurrency = (value: number | null | undefined, digits = 0) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const FilterButton = ({ label }: { label: string }) => (
  <button className={styles.filterButton} type="button">
    <Calendar size={14} />
    {label}
  </button>
);

const BuyAdminRevenueReports = () => {
  const [reports, setReports] = useState<BuyRevenueReportsResponse>(emptyReports);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const loadReports = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await buyAdminApi.getRevenueReports();
      setReports(response);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load revenue reports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const currentMonth = reports.currentMonth || emptyMonth;
  const mrrGrowth = reports.mrrGrowth || 0;

  const exportCsv = () => {
    const headers = ["Month", "MRR", "ARR", "New MRR", "Churn MRR", "Net MRR", "Members"];
    const rows = reports.monthlyData.map((row) => [
      row.month,
      row.mrr,
      row.arr,
      row.newMrr,
      row.churnMrr,
      row.netMrr,
      row.members,
    ]);
    const csv = [headers, ...rows].map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "revenue_reports.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`${styles.page} buy-admin-theme-scope`} data-testid="revenue-reports-page">
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1>Revenue Reports</h1>
          <p>Financial insights and reports</p>
        </div>
        <div className={`${styles.headerActions} buy-admin-theme-actions`}>
          <FilterButton label={loading ? "Loading" : "Filter"} />
          <button className={styles.exportButton} type="button" onClick={exportCsv}>
            <Download size={16} />
            Export
          </button>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => void loadReports()}
            disabled={loading}
            aria-label="Refresh revenue reports"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? styles.spin : undefined} />
          </button>
          <BuyAdminThemeToggle iconOnly />
        </div>
      </div>

      {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

      <div className={styles.metricsGrid}>
        <section className={`${styles.metricCard} ${styles.emeraldCard} buy-admin-stat-card buy-admin-stat-card--green`}>
          <div className="buy-admin-stat-head">
            <div className="buy-admin-stat-icon">
              <DollarSign size={18} />
            </div>
            <span className="buy-admin-stat-label">Monthly Recurring Revenue</span>
          </div>
          <strong>{formatCurrency(currentMonth.mrr)}</strong>
          <em className={mrrGrowth >= 0 ? styles.greenText : styles.redText}>
            {mrrGrowth >= 0 ? "↑" : "↓"} {Math.abs(mrrGrowth).toFixed(1)}% <small>vs last month</small>
          </em>
        </section>
        <section className={`${styles.metricCard} ${styles.blueCard} buy-admin-stat-card buy-admin-stat-card--blue`}>
          <div className="buy-admin-stat-head">
            <div className="buy-admin-stat-icon">
              <TrendingUp size={18} />
            </div>
            <span className="buy-admin-stat-label">Annual Run Rate</span>
          </div>
          <strong>{formatCurrency(currentMonth.arr)}</strong>
          <em>Based on current MRR</em>
        </section>
        <section className={`${styles.metricCard} ${styles.purpleCard} buy-admin-stat-card buy-admin-stat-card--purple`}>
          <div className="buy-admin-stat-head">
            <div className="buy-admin-stat-icon">
              <Activity size={18} />
            </div>
            <span className="buy-admin-stat-label">Net MRR Growth</span>
          </div>
          <strong>{currentMonth.netMrr >= 0 ? "+" : "-"}{formatCurrency(Math.abs(currentMonth.netMrr))}</strong>
          <em>New: +{formatCurrency(currentMonth.newMrr)} | Churn: -{formatCurrency(currentMonth.churnMrr)}</em>
        </section>
        <section className={`${styles.metricCard} ${styles.amberCard} buy-admin-stat-card buy-admin-stat-card--amber`}>
          <div className="buy-admin-stat-head">
            <div className="buy-admin-stat-icon">
              <Users size={18} />
            </div>
            <span className="buy-admin-stat-label">ARPU</span>
          </div>
          <strong>{formatCurrency(currentMonth.members ? currentMonth.mrr / currentMonth.members : 0, 2)}</strong>
          <em>Avg Revenue Per User</em>
        </section>
      </div>

      <section className={styles.card}>
        <h2>MRR Growth Trend</h2>
        <div className={styles.chartPanel}>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={reports.monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--buy-chart-grid)" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value: number | string) => formatCurrency(Number(value))} />
              <Legend />
              <Area type="monotone" dataKey="mrr" stroke="#10b981" fill="#10b981" fillOpacity={0.3} name="MRR ($)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className={styles.twoColumnGrid}>
        <section className={styles.card}>
          <h2>Revenue Breakdown</h2>
          <div className={styles.breakdownStack}>
            {reports.revenueBreakdown.map((item, index) => (
              <div key={item.source} className={styles.breakdownItem}>
                <div className={styles.breakdownMeta}>
                  <span>{item.source}</span>
                  <strong>{formatCurrency(item.amount)} ({item.pct}%)</strong>
                </div>
                <div className={styles.progressTrack}>
                  <div
                    className={
                      index === 0 ? styles.progressEmerald
                        : index === 1 ? styles.progressBlue
                          : index === 2 ? styles.progressPurple
                            : styles.progressSlate
                    }
                    style={{ width: `${Math.max(2, item.pct)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.card}>
          <h2>MRR Movement (This Month)</h2>
          <div className={styles.movementStack}>
            <div className={`${styles.movementRow} ${styles.movementGreen}`}>
              <span><TrendingUp size={20} /> New MRR</span>
              <strong>+{formatCurrency(currentMonth.newMrr)}</strong>
            </div>
            <div className={`${styles.movementRow} ${styles.movementRed}`}>
              <span><TrendingDown size={20} /> Churned MRR</span>
              <strong>-{formatCurrency(currentMonth.churnMrr)}</strong>
            </div>
            <div className={`${styles.movementRow} ${styles.movementBlue}`}>
              <span><Activity size={20} /> Net MRR Change</span>
              <strong>{currentMonth.netMrr >= 0 ? "+" : "-"}{formatCurrency(Math.abs(currentMonth.netMrr))}</strong>
            </div>
          </div>
        </section>
      </div>

      <section className={styles.card}>
        <h2>Monthly Revenue Report</h2>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Month</th>
                <th>MRR</th>
                <th>ARR</th>
                <th>New MRR</th>
                <th>Churn MRR</th>
                <th>Net MRR</th>
                <th>Members</th>
              </tr>
            </thead>
            <tbody>
              {reports.monthlyData.map((row) => (
                <tr key={row.month}>
                  <td>{row.month}</td>
                  <td>{formatCurrency(row.mrr)}</td>
                  <td>{formatCurrency(row.arr)}</td>
                  <td className={styles.greenText}>+{formatCurrency(row.newMrr)}</td>
                  <td className={styles.redText}>-{formatCurrency(row.churnMrr)}</td>
                  <td className={row.netMrr >= 0 ? styles.greenText : styles.redText}>
                    {row.netMrr >= 0 ? "+" : "-"}{formatCurrency(Math.abs(row.netMrr))}
                  </td>
                  <td>{row.members.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default BuyAdminRevenueReports;
